import { useState, useEffect, useRef } from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Send, Mail, ChevronLeft, CalendarIcon, Clock,
  X, User, MessageSquare, Shield, ChevronRight,
  CheckCircle2, Sparkles, Coins,
} from "lucide-react";
import { format, addDays, isSameDay, startOfDay } from "date-fns";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { BackLink } from "@/components/layout/BackLink";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth-compat";
import { useCreateInquiry } from "@/hooks/use-inquiries";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useStudentProfile } from "@/contexts/StudentProfileContext";
import { getPublicTeacherProfileById } from "@/services/teacherProfileService";
import type { PreferredTimeSlot } from "@/services/inquiryService";

const TIME_SLOTS = [
  "8:00–9:00", "9:00–10:00", "10:00–11:00", "11:00–12:00",
  "12:00–13:00", "13:00–14:00", "14:00–15:00", "15:00–16:00",
  "16:00–17:00", "17:00–18:00", "18:00–19:00", "19:00–20:00",
];

const MAX_SLOTS = 5;

// ─── Step label ──────────────────────────────────────────────────────────────
function StepLabel({ n, label }: { n: number; label: string }) {
  return (
    <div className="flex items-center gap-2.5 mb-4">
      <div className="w-6 h-6 rounded-full bg-[hsl(210,60%,18%)] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
        {n}
      </div>
      <span className="text-sm font-bold text-slate-700">{label}</span>
    </div>
  );
}

// ─── Main ────────────────────────────────────────────────────────────────────
export default function SendInquiryPage() {
  usePageTitle('Send Inquiry | BookMyCoaching');
  const { id: teacherId } = useParams<{ id: string }>();
  const navigate   = useNavigate();
  const location   = useLocation();
  const auth       = useAuth();
  const { user }   = useCurrentUser();

  const createInquiry = useCreateInquiry();

  // StudentProfileProvider wraps the whole app (see App.tsx), so this hook is always
  // available here. coinBalance is null when the student never received a balance
  // grant — in that case we simply omit the pill below rather than showing "null".
  const { profile: studentProfile } = useStudentProfile();
  const coinBalance = studentProfile?.coinBalance ?? null;

  const [message,      setMessage]      = useState("");
  const [shareContact, setShareContact] = useState(false);
  const [selectedSlots, setSelectedSlots] = useState<PreferredTimeSlot[]>([]);
  const [selectedDate,  setSelectedDate]  = useState<Date | undefined>();
  const dateStripRef = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [teacher,        setTeacher]        = useState<any>(null);
  const [teacherLoading, setTeacherLoading] = useState(true);

  const hasLocalIdentity = document.cookie.includes("X-Local-Identity=");
  const isAuthenticated  = auth.isAuthenticated || hasLocalIdentity;

  useEffect(() => {
    const stateTeacher = (location.state as any)?.teacher;
    if (stateTeacher) { setTeacher(stateTeacher); setTeacherLoading(false); return; }
    if (!teacherId) return;
    setTeacherLoading(true);
    getPublicTeacherProfileById(teacherId)
      .then((data) => { if (data) setTeacher(data); })
      .catch(() => {})
      .finally(() => setTeacherLoading(false));
  }, [teacherId, location.state]);

  const scrollDates = (dir: "left" | "right") => {
    dateStripRef.current?.scrollBy({ left: dir === "right" ? 180 : -180, behavior: "smooth" });
  };

  if (!isAuthenticated) {
    navigate("/login", { state: { returnTo: `/teacher/${teacherId}/inquiry` } });
    return null;
  }

  const dateStrip   = Array.from({ length: 14 }, (_, i) => addDays(startOfDay(new Date()), i + 1));
  const teacherName = teacher?.fullName || teacher?.teacherName || teacher?.name || "Teacher";
  const teacherImage = teacher?.profileImage || teacher?.profileImageUrl || teacher?.teacherProfileImageUrl;
  const teacherBio  = teacher?.professionalBio;
  const teacherSubjects: string[] = teacher?.subjects ?? [];

  const handleAddSlot = (time: string) => {
    if (!selectedDate) return;
    if (selectedSlots.length >= MAX_SLOTS) {
      toast({ title: `Maximum ${MAX_SLOTS} time slots allowed`, variant: "destructive" });
      return;
    }
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    if (selectedSlots.some(s => s.date === dateStr && s.timeSlot === time)) return;
    setSelectedSlots([...selectedSlots, { date: dateStr, timeSlot: time }]);
  };

  const handleRemoveSlot = (index: number) =>
    setSelectedSlots(selectedSlots.filter((_, i) => i !== index));

  const handleSubmit = async () => {
    if (!teacherId || !message.trim()) {
      toast({ title: "Please write a message before sending", variant: "destructive" });
      return;
    }
    try {
      const thread = await createInquiry.mutateAsync({
        teacherUserId: teacherId,
        subject: `Inquiry to ${teacherName}`,
        message: message.trim(),
        shareContactInfo: shareContact,
        preferredTimeSlots: selectedSlots,
      });
      toast({ title: "Inquiry sent!", description: "The teacher will be notified shortly." });
      navigate(`/inquiry/${thread.id}`);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : "Failed to send inquiry";
      if (msg === "THREAD_EXISTS") {
        toast({ title: "You already have an active conversation with this teacher", variant: "destructive" });
        return;
      }
      if (msg === "RATE_LIMITED") {
        toast({ title: "Too many requests", description: "Please try again in a moment.", variant: "destructive" });
        return;
      }
      if (msg === "INSUFFICIENT_COINS") {
        toast({
          title: "Out of BMC coins",
          description: "You're out of BMC coins and can't send a new inquiry right now. Go to My Coins → Buy Coins to top up.",
          variant: "destructive",
        });
        return;
      }
      toast({ title: "Error", description: msg, variant: "destructive" });
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFB]">
      <Header />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <div className="pt-20">
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[hsl(197,52%,92%)] via-[hsl(197,52%,96%)] to-[#F8FAFB]" />
          <div className="absolute inset-0 opacity-[0.035]" style={{
            backgroundImage: "radial-gradient(hsl(210,60%,18%) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }} />
          <div className="absolute -top-24 -right-24 w-[400px] h-[400px] bg-[hsl(210,60%,38%)]/10 rounded-full blur-[100px] pointer-events-none" />

          <div className="relative z-10 max-w-5xl mx-auto px-6 pt-10 pb-12">
            <BackLink label="Back" onClick={() => navigate(-1)} className="mb-8" />

            <div className="flex items-start gap-5">
              {/* Avatar */}
              <div className="shrink-0">
                {teacherLoading ? (
                  <div className="w-16 h-16 rounded-2xl bg-slate-200 animate-pulse" />
                ) : teacherImage ? (
                  <img
                    src={teacherImage}
                    alt={teacherName}
                    className="w-16 h-16 rounded-2xl object-cover border-2 border-white shadow-md"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-2xl bg-[hsl(210,60%,18%)]/10 flex items-center justify-center">
                    <User className="w-7 h-7 text-[hsl(210,60%,28%)]" />
                  </div>
                )}
              </div>

              {/* Title */}
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-[hsl(210,60%,38%)] mb-1">
                  Send an inquiry to
                </p>
                <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 leading-tight">
                  {teacherLoading ? (
                    <span className="inline-block w-48 h-7 bg-slate-200 rounded animate-pulse" />
                  ) : teacherName}
                </h1>
                {teacherBio && (
                  <p className="text-sm text-slate-400 mt-1 max-w-lg line-clamp-2">{teacherBio}</p>
                )}
                {teacherSubjects.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {teacherSubjects.slice(0, 4).map((s: string) => (
                      <span key={s} className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[hsl(210,60%,18%)]/8 text-[hsl(210,60%,28%)]">
                        {s}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── BODY: two-column ─────────────────────────────────────────────── */}
      <main className="max-w-5xl mx-auto px-6 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">

          {/* ── LEFT: form (2/3 width) ─────────────────────────────────── */}
          <div className="lg:col-span-2 space-y-5">

            {/* Step 1 — Message */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <StepLabel n={1} label="Write your message" />
              <p className="text-xs text-slate-400 mb-4 leading-relaxed">
                Introduce yourself and describe what you need. Mention your subject, level, and any specific goals — detailed messages get faster responses.
              </p>
              <Textarea
                id="message"
                placeholder={`Hi ${teacherName}, I'm interested in your teaching. I'm a [level] student looking for help with…`}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={7}
                className="resize-none border-slate-200 focus-visible:ring-[hsl(210,60%,18%)]/20 focus-visible:border-[hsl(210,60%,28%)] text-sm placeholder:text-slate-300 leading-relaxed"
              />
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-slate-300">Tip: be specific about your level and goals.</p>
                <p className={cn("text-xs font-medium", message.length > 800 ? "text-rose-500" : "text-slate-300")}>
                  {message.length} chars
                </p>
              </div>
            </div>

            {/* Step 2 — Preferred availability */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <StepLabel n={2} label="Preferred availability" />
              <p className="text-xs text-slate-400 mb-4">
                Optional — pick up to {MAX_SLOTS} time slots so the teacher can suggest a session that works for you.
              </p>

              {/* Date strip */}
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-slate-500 flex items-center gap-1.5">
                  <CalendarIcon className="w-3.5 h-3.5 text-[hsl(210,60%,28%)]" />
                  Pick a date
                </p>
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="inline-flex items-center gap-1 text-xs font-semibold text-[hsl(210,60%,28%)] hover:text-[hsl(210,60%,18%)] transition-colors">
                      <CalendarIcon className="w-3 h-3" /> Other date
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(d) => setSelectedDate(d)}
                      disabled={(date) => date <= new Date()}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="flex items-center gap-2 mb-4">
                <button
                  onClick={() => scrollDates("left")}
                  className="w-7 h-7 rounded-lg border border-slate-200 bg-white flex items-center justify-center text-slate-400 hover:text-[hsl(210,60%,18%)] hover:border-[hsl(210,60%,28%)] transition-all shrink-0"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div
                  ref={dateStripRef}
                  className="flex flex-nowrap gap-2 overflow-x-auto flex-1"
                  style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                >
                  {dateStrip.map((date) => {
                    const isSelected = selectedDate ? isSameDay(date, selectedDate) : false;
                    return (
                      <button
                        key={date.toISOString()}
                        onClick={() => setSelectedDate(date)}
                        className={cn(
                          "flex flex-col items-center justify-center rounded-xl border text-center transition-all flex-none w-[52px] py-2.5",
                          isSelected
                            ? "bg-[hsl(210,60%,18%)] border-[hsl(210,60%,18%)] text-white shadow-md"
                            : "bg-white border-slate-200 hover:border-[hsl(210,60%,28%)] hover:bg-[hsl(210,60%,18%)]/5"
                        )}
                      >
                        <span className={cn("text-xs font-semibold uppercase tracking-wide", isSelected ? "text-white/70" : "text-slate-400")}>
                          {format(date, "EEE")}
                        </span>
                        <span className={cn("text-lg font-bold leading-tight", isSelected ? "text-white" : "text-slate-800")}>
                          {format(date, "d")}
                        </span>
                        <span className={cn("text-xs font-medium", isSelected ? "text-white/70" : "text-slate-400")}>
                          {format(date, "MMM")}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => scrollDates("right")}
                  className="w-7 h-7 rounded-lg border border-slate-200 bg-white flex items-center justify-center text-slate-400 hover:text-[hsl(210,60%,18%)] hover:border-[hsl(210,60%,28%)] transition-all shrink-0"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {selectedDate && (
                <p className="text-xs text-slate-500 mb-3 flex items-center gap-1.5">
                  <span>Time slots for</span>
                  <span className="font-semibold text-slate-700">{format(selectedDate, "EEEE, d MMMM")}</span>
                  <button onClick={() => setSelectedDate(undefined)} className="text-slate-300 hover:text-rose-400 transition-colors">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </p>
              )}

              {selectedDate ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {TIME_SLOTS.map((time) => {
                    const dateStr    = format(selectedDate, "yyyy-MM-dd");
                    const isSelected = selectedSlots.some(s => s.date === dateStr && s.timeSlot === time);
                    const isDisabled = !isSelected && selectedSlots.length >= MAX_SLOTS;
                    return (
                      <button
                        key={time}
                        onClick={() => isSelected
                          ? handleRemoveSlot(selectedSlots.findIndex(s => s.date === dateStr && s.timeSlot === time))
                          : handleAddSlot(time)
                        }
                        disabled={isDisabled}
                        className={cn(
                          "py-2 px-2 text-xs font-medium rounded-lg border transition-all",
                          isSelected
                            ? "bg-[hsl(210,60%,18%)] text-white border-[hsl(210,60%,18%)] shadow-sm"
                            : isDisabled
                            ? "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed"
                            : "bg-white text-slate-600 border-slate-200 hover:border-[hsl(210,60%,28%)] hover:text-[hsl(210,60%,18%)] hover:bg-[hsl(210,60%,18%)]/5"
                        )}
                      >
                        {time}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center gap-2 py-5 justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/50">
                  <Clock className="w-4 h-4 text-slate-300" />
                  <p className="text-xs text-slate-400">Select a date to see available time slots</p>
                </div>
              )}

              {/* Selected slots chips */}
              {selectedSlots.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Selected slots</p>
                    <span className="text-xs font-semibold text-[hsl(210,60%,28%)] bg-[hsl(210,60%,18%)]/8 px-2 py-0.5 rounded-full">
                      {selectedSlots.length}/{MAX_SLOTS}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedSlots.map((slot, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-[hsl(210,60%,18%)]/8 text-[hsl(210,60%,18%)] border border-[hsl(210,60%,18%)]/15"
                      >
                        <CalendarIcon className="w-3 h-3" />
                        {format(new Date(slot.date), "d MMM")} · {slot.timeSlot}
                        <button onClick={() => handleRemoveSlot(i)} className="hover:text-rose-500 transition-colors ml-0.5">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Step 3 — Share contact */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <StepLabel n={3} label="Contact sharing" />
              <div className="flex items-start gap-3">
                <Checkbox
                  id="shareContact"
                  checked={shareContact}
                  onCheckedChange={(v) => setShareContact(v === true)}
                  className="mt-0.5 data-[state=checked]:bg-[hsl(210,60%,18%)] data-[state=checked]:border-[hsl(210,60%,18%)]"
                />
                <div className="flex-1">
                  <Label htmlFor="shareContact" className="text-sm font-semibold text-slate-700 cursor-pointer">
                    Share my email address with {teacherName}
                  </Label>
                  <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">
                    This lets the teacher reach you directly outside the platform if needed. You can always message within the platform without sharing.
                  </p>
                  {shareContact && user?.email && (
                    <div className="flex items-center gap-2 mt-3 px-3 py-2 bg-[hsl(210,60%,18%)]/5 rounded-lg border border-[hsl(210,60%,18%)]/15 w-fit">
                      <Mail className="w-3.5 h-3.5 text-[hsl(210,60%,28%)]" />
                      <span className="text-sm font-medium text-slate-700">{user.email}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Trust note */}
            <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-100">
              <Shield className="w-4 h-4 text-emerald-600 shrink-0" />
              <p className="text-xs text-emerald-700 leading-relaxed">
                Your inquiry is private. Only {teacherName} will see your message. You can withdraw it at any time.
              </p>
            </div>

            {/* Coin balance */}
            {coinBalance != null && (
              <div className="flex items-center justify-end">
                <div
                  className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-500"
                  title="Your BMC coin balance"
                >
                  <Coins className="w-3 h-3 text-slate-400" />
                  {coinBalance}
                </div>
              </div>
            )}

            {/* Submit */}
            <Button
              className="w-full h-13 text-sm font-bold gap-2.5 bg-[hsl(210,60%,18%)] hover:bg-[hsl(210,60%,13%)] text-white shadow-lg shadow-[hsl(210,60%,18%)]/25 rounded-xl py-3.5"
              onClick={handleSubmit}
              disabled={createInquiry.isPending || !message.trim()}
            >
              {createInquiry.isPending ? (
                <>
                  <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Inquiry to {teacherName}
                </>
              )}
            </Button>
          </div>

          {/* ── RIGHT: sticky sidebar ──────────────────────────────────── */}
          <div className="space-y-4 lg:sticky lg:top-24">

            {/* Teacher card */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">You're contacting</p>
              <div className="flex items-center gap-3 mb-4">
                {teacherLoading ? (
                  <div className="w-12 h-12 rounded-xl bg-slate-200 animate-pulse shrink-0" />
                ) : teacherImage ? (
                  <img src={teacherImage} alt={teacherName} className="w-12 h-12 rounded-xl object-cover border border-slate-100 shadow-sm shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-[hsl(210,60%,18%)]/10 flex items-center justify-center shrink-0">
                    <User className="w-5 h-5 text-[hsl(210,60%,28%)]" />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="font-bold text-slate-800 text-sm leading-snug truncate">
                    {teacherLoading ? <span className="block w-32 h-4 bg-slate-200 rounded animate-pulse" /> : teacherName}
                  </p>
                  {teacherBio && <p className="text-xs text-slate-400 line-clamp-2 mt-0.5">{teacherBio}</p>}
                </div>
              </div>

              <div className="flex items-center gap-1.5 text-xs text-emerald-600 font-semibold bg-emerald-50 border border-emerald-100 px-3 py-2 rounded-lg">
                <Sparkles className="w-3 h-3" />
                Verified teacher
              </div>
            </div>

            {/* What happens next */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">What happens next</p>
              <div className="space-y-4">
                {[
                  { icon: MessageSquare, title: "Teacher is notified",   body: `${teacherName} reviews your inquiry and any time slots you've shared.` },
                  { icon: CheckCircle2,  title: "They respond",          body: "You'll get a reply with their availability and next steps." },
                  { icon: CalendarIcon,  title: "Book your first session", body: "Agree on a schedule and start learning." },
                ].map(({ icon: Icon, title, body }, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-[hsl(210,60%,18%)]/8 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon className="w-3.5 h-3.5 text-[hsl(210,60%,28%)]" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-700">{title}</p>
                      <p className="text-xs text-slate-400 leading-relaxed mt-0.5">{body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tips */}
            <div className="bg-[hsl(210,60%,18%)]/4 border border-[hsl(210,60%,18%)]/10 rounded-2xl p-5">
              <p className="text-xs font-bold uppercase tracking-widest text-[hsl(210,60%,28%)] mb-3">Tips for a great inquiry</p>
              <ul className="space-y-2">
                {[
                  "State your subject and current level",
                  "Mention your goals or upcoming exams",
                  "Include how many sessions per week you'd like",
                  "Add preferred time slots so they can respond faster",
                ].map((tip) => (
                  <li key={tip} className="flex items-start gap-2 text-xs text-[hsl(210,60%,22%)]">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[hsl(210,60%,38%)] mt-0.5 flex-shrink-0" />
                    {tip}
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
